// The bridge between the player's clock setting and the world's own physics.
//
// Two separate things that have to agree, and the split matters:
//
//   settings.ts   what the player last chose, persisted to localStorage
//   world.physics what the simulation is actually running on
//
// The **world is the authority** everywhere in the game -- `isTurnMode()` reads
// `physics.clock.msPerTurn`, never the setting. That is not pedantry. A playtest
// launched from the World Editor arrives carrying a whole `PhysicsConfig`
// (see playtest.ts), clock included, and a room authored to be verified on the
// turn clock has to be verified on the turn clock regardless of what the tester's
// own menu happens to remember from yesterday. Reading the setting directly would
// quietly hand them the other simulation.
//
// The setting reaches the world through `applyClockSetting`, called once at boot
// and again whenever the option is toggled -- and deliberately *not* at boot
// during a playtest, so the payload's choice survives. Toggling the option
// mid-playtest still works and still wins, which is right: that is the tester
// deliberately asking the other question.
import { getWorld } from './activeWorld';
import { DEFAULT_MS_PER_TURN, isTurnClock } from '../../rune-lab/src/sim/physics';
import { getClockMode } from './settings';
import { isPlaytest, playtestPayload } from './playtest';

/** Whether the world is advancing on player actions rather than elapsed real
 * time. Every gate in the game asks this, not the setting. */
export function isTurnMode(): boolean {
  return isTurnClock(getWorld().physics);
}

/** Writes the player's persisted choice into the live world.
 *
 * Idempotent, and safe to call on a world that is mid-play: `msPerTurn` is read
 * fresh at each action rather than captured, so switching clocks takes effect on
 * the very next thing the player does. The accumulators (`sinceManaRegenMs` and
 * friends) are deliberately left alone -- a world switched to the turn clock
 * halfway through a decay interval resumes that interval where it was, which is
 * the same thing `state.ts` already says about a saved or branched world. */
export function applyClockSetting(): void {
  getWorld().physics.clock.msPerTurn = getClockMode() === 'turn' ? DEFAULT_MS_PER_TURN : 0;
}

/** The boot-time sync: the setting applies to an ordinary session, and a
 * playtest keeps whatever clock its payload was built with. */
export function initClockMode(): void {
  if (isPlaytest() && playtestPayload()) return;
  applyClockSetting();
}
